import {Tooltip} from 'platform/components';

import type {Nullish} from 'shared/web';

import type {CellRendererProps} from '../../types/CellRendererProps';
import type {TooltipGetter} from '../../types/TooltipGetter';
import {getCellClassName} from '../../utils/getCellClassName';
import {getDisplayValue} from '../../utils/getDisplayValue';
import {getTooltipProps} from '../../utils/getTooltipProps';
import {parseMetaText} from '../../utils/parseMetaText';

// META_TEXT columns are TEXT columns with a METATEXT renderer, so the API sends
// the cell value as a plain string (optionally carrying `@[Name](id)` mention
// markup) — never a wrapper object. getValueFromRawCell hands that string
// straight through.
type MetaTextCellRendererProps = CellRendererProps<string | Nullish> & {
  getTooltipProps?: TooltipGetter;
};

export function MetaTextCellRenderer(props: MetaTextCellRendererProps) {
  const className = getCellClassName(props.isWrap);
  const tooltipProps = getTooltipProps(props.row, props.getTooltipProps);

  // renderCell dispatches renderers as ComponentType<any> and passes the unvalidated
  // value straight from getValueFromRawCell, so the prop type is not enforced at
  // runtime. Guard the type here: an unexpected non-string would otherwise throw in
  // getDisplayValue's .trim() and take down the whole grid render instead of one cell.
  const textValue = typeof props.value === 'string' ? props.value : null;

  // getDisplayValue resolves nil / blank values to the empty-cell placeholder
  // ('' or '–'), matching PlainCellRenderer; a real value is parsed for mentions.
  const displayValue = getDisplayValue(textValue, props.emptyCellValue);

  return (
    <Tooltip {...tooltipProps}>
      <div className={className}>{parseMetaText(displayValue)}</div>
    </Tooltip>
  );
}
